// Experiments View: View experiment details with tabs

import { Button } from "@/components/button";
import { LoadingSpinner } from "@/components/loading-spinner";
import { Separator } from "@/components/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { useExperiment, useExperimentCampaigns } from "@/http/api-hooks";
import { Experiment, ExperimentTag } from "@/http/api-types";
import { ArrowLeft } from "lucide-react";
import { useMemo, type ComponentProps } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ExperimentForm, ExperimentFormValues, getExperimentFormDefaultValues } from "./components/ExperimentForm";
import { ExperimentHeader } from "./components/ExperimentHeader";
import { ExperimentNotes } from "./components/ExperimentNotes";
import { DiagnosticsTab } from "./components/tabs/DiagnosticsTab";
import { ExploreTab } from "./components/tabs/ExploreTab";
import { HistoryTab } from "./components/tabs/HistoryTab";
import { ModelHealthTab } from "./components/tabs/ModelHealthTab";
import { ResultsTab } from "./components/tabs/ResultsTab";
import { SetupTab } from "./components/tabs/SetupTab";
import { SummaryTab } from "./components/tabs/SummaryTab";

type ExperimentTab = "setup" | "diagnostics" | "model-health" | "results" | "explore" | "summary" | "history";

const EDITABLE_TABS: ExperimentTab[] = ["setup", "summary"];

function isExperimentTab(value: string | null): value is ExperimentTab {
  return (
    value === "setup" ||
    value === "diagnostics" ||
    value === "model-health" ||
    value === "results" ||
    value === "explore" ||
    value === "summary" ||
    value === "history"
  );
}

function isEditableTab(tab: ExperimentTab): boolean {
  return EDITABLE_TABS.includes(tab);
}

type ExperimentViewContentProps = {
  experimentId: number;
  activeTab: ExperimentTab;
  isEditMode: boolean;
  canEditCurrentTab: boolean;
  onEditClick: () => void;
  onCancelClick: () => void;
  onTabChange: NonNullable<ComponentProps<typeof Tabs>["onValueChange"]>;
};

// Wrapper component that renders the view with form state for edit mode
function ExperimentViewContent({
  experimentId,
  activeTab,
  isEditMode,
  canEditCurrentTab,
  onEditClick,
  onCancelClick,
  onTabChange,
}: ExperimentViewContentProps) {
  const navigate = useNavigate();

  // Fetch the experiment and campaigns
  const { data: experiment, isLoading: experimentLoading, mutate: mutateExperiment } = useExperiment(experimentId);
  const { data: existingCampaigns, isLoading: campaignsLoading } = useExperimentCampaigns(experimentId);
  const sharedFormDefaultValues = useMemo(
    () => getExperimentFormDefaultValues(experiment, existingCampaigns),
    [experiment, existingCampaigns],
  );
  const sharedFormMethods = useForm<ExperimentFormValues>({
    defaultValues: sharedFormDefaultValues,
    values: sharedFormDefaultValues,
  });

  const handleCancelEdit = () => {
    sharedFormMethods.reset(sharedFormDefaultValues, {
      keepDirty: false,
      keepTouched: false,
    });
    onCancelClick();
  };

  // Tags save outside the experiment edit form, so patch the cached experiment with what
  // the tags endpoint returned instead of refetching the whole record.
  const handleTagsSaved = (tags: ExperimentTag[]) => {
    mutateExperiment(current => (current ? { ...current, tags } : current), { revalidate: false });
  };

  const handleSaveSuccess = (updatedExperiment?: Experiment) => {
    // After save, exit edit mode and refresh data
    if (updatedExperiment) {
      mutateExperiment(updatedExperiment, { revalidate: false });
    } else {
      mutateExperiment();
    }
    onCancelClick();
  };

  // Loading state
  if (experimentLoading || campaignsLoading) {
    return (
      <div>
        <div className="w-full flex justify-center py-6">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Not found
  if (!experiment) {
    return (
      <div>
        <div className="text-red-600">Experiment not found</div>
        <Button variant="outline" onClick={() => navigate("/experiments")} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Experiments
        </Button>
      </div>
    );
  }

  return (
    <>
      <ExperimentHeader
        experiment={experiment}
        isEditMode={isEditMode}
        canEdit={canEditCurrentTab}
        onEditClick={onEditClick}
        onCancelClick={handleCancelEdit}
        onTagsSaved={handleTagsSaved}
      />

      <Separator className="my-6" />

      <FormProvider {...sharedFormMethods}>
        <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="setup" disabled={isEditMode && activeTab !== "setup"}>
              Setup
            </TabsTrigger>
            <TabsTrigger value="diagnostics" disabled={isEditMode && activeTab !== "diagnostics"}>
              Diagnostics
            </TabsTrigger>
            <TabsTrigger value="model-health" disabled={isEditMode && activeTab !== "model-health"}>
              Model Health
            </TabsTrigger>
            <TabsTrigger value="results" disabled={isEditMode && activeTab !== "results"}>
              Results
            </TabsTrigger>
            <TabsTrigger value="explore" disabled={isEditMode && activeTab !== "explore"}>
              Explore
            </TabsTrigger>
            <TabsTrigger value="summary" disabled={isEditMode && activeTab !== "summary"}>
              Summary
            </TabsTrigger>
            <TabsTrigger value="history" disabled={isEditMode && activeTab !== "history"}>
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="setup">
            {isEditMode ? (
              <ExperimentForm
                experimentId={experimentId}
                isEditing={true}
                initialExperiment={experiment}
                initialCampaigns={existingCampaigns}
                onSaveSuccess={handleSaveSuccess}
                onCancel={handleCancelEdit}
                showCancelButton={false}
                formMethods={sharedFormMethods}
              />
            ) : (
              <SetupTab experiment={experiment} />
            )}
          </TabsContent>

          <TabsContent value="diagnostics">
            <DiagnosticsTab experiment={experiment} />
          </TabsContent>

          <TabsContent value="model-health">
            <ModelHealthTab experiment={experiment} />
          </TabsContent>

          <TabsContent value="results">
            <ResultsTab experiment={experiment} />
          </TabsContent>

          <TabsContent value="explore">
            <ExploreTab />
          </TabsContent>

          <TabsContent value="summary">
            <SummaryTab
              experiment={experiment}
              isEditMode={isEditMode}
              initialCampaigns={existingCampaigns}
              onSaveSuccess={handleSaveSuccess}
              onCancel={handleCancelEdit}
            />
          </TabsContent>

          <TabsContent value="history">
            <HistoryTab experimentId={experiment.id} />
          </TabsContent>
        </Tabs>

        <div id="notes" className="mt-10">
          <ExperimentNotes experimentId={experiment.id} />
        </div>
      </FormProvider>
    </>
  );
}

export default function ExperimentsView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const experimentId = id ? parseInt(id, 10) : null;
  const tabParam = searchParams.get("tab");
  const activeTab: ExperimentTab = isExperimentTab(tabParam) ? tabParam : "setup";
  const isEditMode = searchParams.get("edit") === "true";
  const canEditCurrentTab = isEditableTab(activeTab);

  const updateSearchParams = (updates: { edit?: string | null; tab?: ExperimentTab | null }) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);

      if (updates.edit === null) {
        next.delete("edit");
      } else if (updates.edit !== undefined) {
        next.set("edit", updates.edit);
      }

      if (updates.tab === null) {
        next.delete("tab");
      } else if (updates.tab !== undefined) {
        next.set("tab", updates.tab);
      }

      return next;
    });
  };

  const handleTabChange = (tab: string) => {
    if (isEditMode) return;
    if (!isExperimentTab(tab)) return;
    updateSearchParams({ tab, edit: null });
  };

  const handleEditClick = () => {
    const tabToEdit = activeTab;
    if (!isEditableTab(tabToEdit)) return;
    updateSearchParams({ edit: "true", tab: tabToEdit });
  };

  const handleCancelClick = () => {
    updateSearchParams({ edit: null, tab: activeTab });
  };

  // If no experiment ID, redirect to list
  if (!experimentId) {
    navigate("/experiments");
    return null;
  }

  return (
    <ExperimentViewContent
      experimentId={experimentId}
      activeTab={activeTab}
      isEditMode={isEditMode}
      canEditCurrentTab={canEditCurrentTab}
      onEditClick={handleEditClick}
      onCancelClick={handleCancelClick}
      onTabChange={handleTabChange}
    />
  );
}
